import { Navbar, Footer, FloatingCTA } from "@/components/Layout";
import {
  HeroSection,
  TrustSection,
  ServicesSection,
  ProcessSection,
  TestimonialsSection,
  FAQSection,
  ContactSection,
} from "@/components/Sections";
import { useEffect } from "react";
import { FIRM_NAME } from "@/data";

function SEOStructuredData(): null {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LegalService",
        "@id": "https://www.trialdefencelaw.com.sg/#organization",
        name: FIRM_NAME,
        description:
          "Experienced civil and criminal claim trial lawyers in Singapore. Contract disputes, debt recovery, property claims, criminal defence, and more.",
        url: "https://www.trialdefencelaw.com.sg",
        telephone: "+6580148989",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Carpenter Street",
          addressLocality: "Singapore",
          addressCountry: "SG",
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "11:00",
            closes: "20:00",
          },
        ],
        serviceArea: { "@type": "Country", name: "Singapore" },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Civil & Criminal Litigation Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Civil Claim Trial" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Criminal Claim Trial" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Contract Dispute Resolution" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Debt Recovery" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Criminal Defence" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Property & Tenancy Disputes" } },
          ],
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is a civil claim trial in Singapore?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A civil claim trial is a court hearing where a judge decides a private dispute between individuals or businesses. The claimant must prove their case on a balance of probabilities.",
            },
          },
          {
            "@type": "Question",
            name: "What is a criminal claim trial in Singapore?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A criminal claim trial is a court proceeding where the Public Prosecutor brings charges against an accused person. The accused is presumed innocent and the prosecution must prove guilt beyond a reasonable doubt.",
            },
          },
          {
            "@type": "Question",
            name: "Which court handles civil claims in Singapore?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Claims up to S$20,000 go to the Small Claims Tribunal; up to S$60,000 to the Magistrate's Court; S$60,000–S$250,000 to the District Court; above S$250,000 to the High Court.",
            },
          },
        ],
      },
    ],
  };

  useEffect(() => {
    document.title =
      "Claim Trial Lawyer Singapore | Civil & Criminal | Trial Defence Law";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const setOG = (prop: string, content: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta(
      "description",
      "Experienced civil and criminal claim trial lawyers in Singapore. Free consultation. Contract disputes, debt recovery, criminal defence, property claims. Call +65 8014 8989."
    );
    setMeta(
      "keywords",
      "claim trial lawyer Singapore, civil litigation Singapore, criminal defence lawyer Singapore, civil claim lawyer, contract dispute Singapore, debt recovery lawyer, criminal trial Singapore"
    );
    setMeta("robots", "index, follow");
    setOG("og:title", "Claim Trial Lawyer Singapore | Trial Defence Law");
    setOG(
      "og:description",
      "Expert civil and criminal claim trial lawyers in Singapore. Free consultation. Call +65 8014 8989."
    );
    setOG("og:type", "website");
    setOG("og:locale", "en_SG");
    setOG("og:url", "https://www.trialdefencelaw.com.sg");

    let script = document.getElementById("schema-org") as HTMLScriptElement;
    if (!script) {
      script = document.createElement("script");
      script.id = "schema-org";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }, []);

  return null;
}

export default function Home() {
  return (
    <>
      <SEOStructuredData />
      <Navbar />
      <main>
        <HeroSection />
        <TrustSection />
        <ServicesSection />
        <ProcessSection />
        <TestimonialsSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
}
